(function () {
  const instances = new WeakMap();

  function escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value || '';
    return element.innerHTML;
  }

  function createMarkerIcon(selected, label) {
    return window.L.divIcon({
      className: 'ast-branch-marker-wrap',
      html: '<span class="ast-branch-marker' + (selected ? ' is-selected' : '') + '"><span>' + escapeHtml(label) + '</span></span>',
      iconSize: selected ? [58, 58] : [48, 48],
      iconAnchor: selected ? [29, 58] : [24, 48],
      popupAnchor: [0, selected ? -58 : -48]
    });
  }

  function popupContent(branch) {
    const root = document.createElement('div');
    root.className = 'ast-branch-popup';

    const title = document.createElement('strong');
    title.textContent = branch.name;
    root.appendChild(title);

    const address = document.createElement('span');
    address.textContent = branch.address;
    root.appendChild(address);

    if (branch.phone) {
      const phone = document.createElement('a');
      phone.href = 'tel:' + branch.phone.replace(/[^+\d]/g, '');
      phone.textContent = branch.phone;
      root.appendChild(phone);
    }

    if (branch.email) {
      const email = document.createElement('a');
      email.href = 'mailto:' + branch.email;
      email.textContent = branch.email;
      root.appendChild(email);
    }

    return root;
  }

  function parseBranches(root) {
    return [...root.querySelectorAll('[data-branch-card]')].map((card, index) => ({
      index,
      card,
      name: card.dataset.branchName || '',
      address: card.dataset.branchAddress || '',
      postalCode: card.dataset.branchPostalCode || '',
      city: card.dataset.branchCity || '',
      phone: card.dataset.branchPhone || '',
      email: card.dataset.branchEmail || '',
      hours: card.dataset.branchHours || '',
      latitude: Number.parseFloat(card.dataset.branchLatitude),
      longitude: Number.parseFloat(card.dataset.branchLongitude),
      marker: null
    })).filter((branch) => Number.isFinite(branch.latitude) && Number.isFinite(branch.longitude));
  }

  function distanceKm(from, to) {
    const radius = 6371;
    const radians = (degrees) => degrees * Math.PI / 180;
    const latitudeDelta = radians(to.latitude - from.latitude);
    const longitudeDelta = radians(to.longitude - from.longitude);
    const a = Math.sin(latitudeDelta / 2) ** 2
      + Math.cos(radians(from.latitude)) * Math.cos(radians(to.latitude))
      * Math.sin(longitudeDelta / 2) ** 2;
    return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function extractPoint(data) {
    const address = data && Array.isArray(data.adresser) ? data.adresser[0] : null;
    const point = address && address.representasjonspunkt;
    if (!point) return null;
    const latitude = Number.parseFloat(point.lat);
    const longitude = Number.parseFloat(point.lon);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
    return { latitude, longitude, city: address.poststed || '' };
  }

  function initialize(root) {
    if (!root || instances.has(root)) return;

    const mapElement = root.querySelector('[data-branch-map]');
    const form = root.querySelector('[data-postal-form]');
    const input = root.querySelector('[data-postal-input]');
    const status = root.querySelector('[data-postal-status]');
    const branches = parseBranches(root);
    const markerLabel = root.dataset.markerLabel || '';

    if (!mapElement || !window.L || branches.length === 0) {
      if (status) status.textContent = root.dataset.mapError || '';
      return;
    }

    const map = window.L.map(mapElement, {
      scrollWheelZoom: false,
      zoomControl: true
    });

    window.L.tileLayer(root.dataset.tileUrl, {
      attribution: root.dataset.tileAttribution,
      maxZoom: 19
    }).addTo(map);

    const bounds = window.L.latLngBounds();
    branches.forEach((branch) => {
      branch.marker = window.L.marker([branch.latitude, branch.longitude], {
        icon: createMarkerIcon(false, markerLabel),
        title: branch.name,
        keyboard: true
      }).addTo(map);
      branch.marker.bindPopup(popupContent(branch), { maxWidth: 280 });
      branch.marker.on('click', () => selectBranch(branch.index, true));
      bounds.extend([branch.latitude, branch.longitude]);
    });

    if (branches.length === 1) {
      map.setView([branches[0].latitude, branches[0].longitude], 14);
    } else {
      map.fitBounds(bounds, { padding: [44, 44], maxZoom: 13 });
    }

    function selectBranch(index, moveMap) {
      const selected = branches.find((branch) => branch.index === Number(index));
      if (!selected) return;

      branches.forEach((branch) => {
        const active = branch === selected;
        branch.card.classList.toggle('is-selected', active);
        branch.card.setAttribute('aria-current', active ? 'true' : 'false');
        branch.marker.setIcon(createMarkerIcon(active, markerLabel));
        branch.marker.setZIndexOffset(active ? 1000 : 0);
      });

      if (moveMap) map.flyTo([selected.latitude, selected.longitude], Math.max(map.getZoom(), 14), { duration: .7 });
      selected.marker.openPopup();
    }

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-select-branch]');
      if (!trigger || !root.contains(trigger)) return;
      selectBranch(trigger.dataset.selectBranch, true);
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const postalCode = (input?.value || '').trim();

      if (!/^\d{4}$/.test(postalCode)) {
        input?.setAttribute('aria-invalid', 'true');
        if (status) {
          status.className = 'branch-search__status is-error';
          status.textContent = root.dataset.invalidPostal;
        }
        return;
      }

      input?.removeAttribute('aria-invalid');
      if (status) {
        status.className = 'branch-search__status is-loading';
        status.textContent = root.dataset.searchingText;
      }

      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 10000);

      try {
        const endpoint = new URL(root.dataset.geocodingEndpoint);
        endpoint.searchParams.set('postnummer', postalCode);
        endpoint.searchParams.set('treffPerSide', '1');
        endpoint.searchParams.set('side', '0');
        const response = await fetch(endpoint.toString(), {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error('Geocoding request failed');

        const point = extractPoint(await response.json());
        if (!point) throw new Error('Postal code not found');

        const nearest = branches.reduce((best, branch) => {
          const distance = distanceKm(point, branch);
          return !best || distance < best.distance ? { branch, distance } : best;
        }, null);

        selectBranch(nearest.branch.index, true);
        if (status) {
          status.className = 'branch-search__status is-success';
          status.textContent = root.dataset.nearestPrefix + ' ' + nearest.branch.name + ' · ' + nearest.distance.toFixed(1).replace('.', ',') + ' km';
        }
      } catch (error) {
        if (status) {
          status.className = 'branch-search__status is-error';
          status.textContent = root.dataset.lookupError;
        }
      } finally {
        window.clearTimeout(timer);
      }
    });

    selectBranch(branches[0].index, false);

    const requestedPostalCode = new URLSearchParams(window.location.search).get('postnummer');
    if (requestedPostalCode && form && input) {
      input.value = requestedPostalCode.replace(/\D/g, '').slice(0, 4);
      window.setTimeout(() => {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }, 80);
    }

    window.setTimeout(() => map.invalidateSize(), 0);
    instances.set(root, { map });
  }

  function initializeAll(scope) {
    (scope || document).querySelectorAll('[data-branch-locator]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
